import { useEffect, useMemo } from 'react';
import { formatTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useSlots } from '../api';
import { WEEKDAYS, addDays, daysInMonth, keyOf, monthGrid, monthTitle, partsOf, todayKey } from '../format';
import type { Slot } from '../types';
import styles from './editor.module.css';

const LONG_DAY = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const SHORT_DAY = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function berlinHour(iso: string): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', hour: '2-digit', hour12: false }).format(
      new Date(iso),
    ),
  );
}

/** `2026-11-02` → „Montag, 2. November 2026". */
export function longDay(dayKey: string): string {
  return LONG_DAY.format(new Date(`${dayKey}T12:00:00Z`));
}

/** „Mo., 02.11.2026 · 11:30–12:15 Uhr" — the chosen slot in one line. */
export function slotSummary(slot: Slot): string {
  return `${SHORT_DAY.format(new Date(slot.startsAt))} · ${formatTime(slot.startsAt)}–${formatTime(slot.endsAt)} Uhr`;
}

export function slotKey(slot: Slot): string {
  return `${slot.startsAt}|${slot.advisorId}`;
}

/** First day of the month a day key falls in. */
export function monthOf(dayKey: string): string {
  const { year, month } = partsOf(dayKey);
  return keyOf(year, month, 1);
}

const GROUPS: ReadonlyArray<{ label: string; test: (hour: number) => boolean }> = [
  { label: 'Vormittag', test: (hour) => hour < 12 },
  { label: 'Nachmittag', test: (hour) => hour >= 12 && hour < 17 },
  { label: 'Abend', test: (hour) => hour >= 17 },
];

export interface SlotPickerProps {
  eventTypeId: string | null;
  /** First day of the shown month (`YYYY-MM-01`). */
  month: string;
  onMonthChange: (month: string) => void;
  day: string | null;
  onDayChange: (day: string | null) => void;
  slot: Slot | null;
  onSlotChange: (slot: Slot) => void;
  emptyHint: string;
}

/**
 * Kontaktakte E4 „Freier Slot": month calendar (days with free times marked) beside the chosen day's
 * times, grouped by part of day. One request per shown month (`GET /slots?internal=1`).
 */
export function SlotPicker({
  eventTypeId,
  month,
  onMonthChange,
  day,
  onDayChange,
  slot,
  onSlotChange,
  emptyHint,
}: SlotPickerProps) {
  const today = todayKey();
  const currentMonth = monthOf(today);
  const { year, month: monthIndex } = partsOf(month);
  const monthEnd = keyOf(year, monthIndex, daysInMonth(year, monthIndex));
  const from = month < today ? today : month;
  const slots = useSlots(eventTypeId && from <= monthEnd ? { eventTypeId, from, to: monthEnd } : null);

  /** Free times per day of the shown month, one entry per start time (round robin: first advisor). */
  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const entry of slots.data?.days ?? []) {
      const seen = new Set<string>();
      const unique = entry.slots.filter((item) => {
        if (seen.has(item.startsAt)) return false;
        seen.add(item.startsAt);
        return true;
      });
      if (unique.length > 0) map.set(entry.date, unique);
    }
    return map;
  }, [slots.data]);

  const loading = Boolean(eventTypeId) && slots.isPending;
  const stale = slots.isPlaceholderData;
  const firstFree = useMemo(() => [...byDay.keys()].sort()[0] ?? null, [byDay]);

  // open the first free day of the month once its data is in (and keep a still-valid choice)
  useEffect(() => {
    if (loading || stale) return;
    if (day && byDay.has(day)) return;
    onDayChange(firstFree);
  }, [loading, stale, day, byDay, firstFree, onDayChange]);

  const times = day ? (byDay.get(day) ?? []) : [];
  const groups = GROUPS.map((group) => ({
    label: group.label,
    items: times.filter((item) => group.test(berlinHour(item.startsAt))),
  })).filter((group) => group.items.length > 0);

  const cells = monthGrid(year, monthIndex);
  const selectedKey = slot ? slotKey(slot) : null;
  const freeDays = byDay.size;

  return (
    <div className={styles.picker}>
      <div className={styles.pickerCalendar}>
        <div className={styles.pickerMonthHead}>
          <span className={styles.pickerMonthTitle}>{monthTitle(year, monthIndex)}</span>
          <div className={styles.pickerMonthNav}>
            <button
              type="button"
              className={styles.pickerNavButton}
              aria-label="Vorheriger Monat"
              disabled={month <= currentMonth}
              onClick={() => onMonthChange(monthOf(addDays(month, -1)))}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.pickerNavButton}
              aria-label="Nächster Monat"
              onClick={() => onMonthChange(monthOf(addDays(monthEnd, 1)))}
            >
              ›
            </button>
          </div>
        </div>
        <div className={styles.pickerGrid} role="grid" aria-label={monthTitle(year, monthIndex)}>
          {WEEKDAYS.map((weekday) => (
            <span key={weekday} className={styles.pickerWeekday} aria-hidden="true">
              {weekday}
            </span>
          ))}
          {cells.map((cell) => {
            if (!cell.inMonth) return <span key={cell.key} className={styles.pickerDayOutside} />;
            const free = byDay.get(cell.key)?.length ?? 0;
            const selected = cell.key === day;
            return (
              <button
                key={cell.key}
                type="button"
                className={clsx(
                  styles.pickerDay,
                  free > 0 && styles.pickerDayFree,
                  cell.key === today && styles.pickerDayToday,
                  selected && styles.pickerDaySelected,
                )}
                disabled={free === 0}
                aria-pressed={selected}
                aria-label={`${longDay(cell.key)}${free > 0 ? `, ${free} freie Zeiten` : ', nichts frei'}`}
                onClick={() => onDayChange(cell.key)}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
        <p className={styles.pickerMonthNote}>
          {loading
            ? ' '
            : freeDays > 0
              ? `${freeDays} Tage mit freien Zeiten`
              : 'In diesem Monat ist nichts frei'}
        </p>
      </div>

      <div className={styles.pickerTimes}>
        {loading ? (
          <div className={styles.pickerTimesBody}>
            <Skeleton width={180} height={18} />
            <div className={styles.pickerTimeGrid}>
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} height={34} />
              ))}
            </div>
          </div>
        ) : slots.isError ? (
          <p className={styles.pickerEmpty}>{errorMessage(slots.error)}</p>
        ) : !eventTypeId ? (
          <p className={styles.pickerEmpty}>Bitte zuerst eine Art wählen.</p>
        ) : day && times.length > 0 ? (
          <div className={clsx(styles.pickerTimesBody, stale && styles.pickerStale)}>
            <div className={styles.pickerDayHead}>
              <span className={styles.pickerDayTitle}>{longDay(day)}</span>
              <span className={styles.pickerDayCount}>
                {times.length === 1 ? '1 freie Zeit' : `${times.length} freie Zeiten`}
              </span>
            </div>
            <div className={styles.pickerGroups} role="radiogroup" aria-label="Freier Slot">
              {groups.map((group) => (
                <div key={group.label} className={styles.pickerGroup}>
                  <span className={styles.pickerGroupLabel}>{group.label}</span>
                  <div className={styles.pickerTimeGrid}>
                    {group.items.map((item) => {
                      const selected = selectedKey === slotKey(item);
                      return (
                        <button
                          key={slotKey(item)}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={clsx(styles.pickerTime, selected && styles.pickerTimeSelected)}
                          onClick={() => onSlotChange(item)}
                        >
                          {formatTime(item.startsAt)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.pickerEmpty}>
            <p>{freeDays === 0 ? emptyHint : 'Bitte einen Tag wählen.'}</p>
            {freeDays === 0 ? (
              <button
                type="button"
                className={styles.ghostSmallInline}
                onClick={() => onMonthChange(monthOf(addDays(monthEnd, 1)))}
              >
                Nächsten Monat ansehen ›
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
